
import { useState } from "react";
import { Globe, ArrowRight, LogOut, LineChart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { PrioritizationPopup } from "./PrioritizationPopup";
import { type Feedback } from "@/types";

type Company = {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  plan: string;
  default_score_method: string | null;
};

type CompanyHeaderProps = {
  company: Company;
  feedbackCount: number;
  feedbackLimit: number;
  feedback: Feedback[];
  onCompanyUpdate: () => Promise<void>;
};

export const CompanyHeader = ({ 
  company, 
  feedbackCount, 
  feedbackLimit,
  feedback,
  onCompanyUpdate
}: CompanyHeaderProps) => {
  const navigate = useNavigate();
  const [prioritizationOpen, setPrioritizationOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/auth');
    } catch (error) {
      console.error('Error logging out:', error);
      toast.error('Failed to log out. Please try again.');
    }
  };

  // Get proper plan display name and badge variant
  const getPlanDetails = () => {
    switch (company.plan) {
      case 'free':
        return { name: 'Free Plan', variant: 'secondary' };
      case 'starter':
        return { name: 'Starter Plan', variant: 'default' };
      case 'enterprise':
        return { name: 'Enterprise Plan', variant: 'default' };
      case 'pro':
        return { name: 'Pro Plan', variant: 'default' };
      default:
        return { name: `${company.plan.charAt(0).toUpperCase() + company.plan.slice(1)} Plan`, variant: 'default' };
    }
  };

  const planDetails = getPlanDetails();

  return (
    <div className="border-b">
      <div className="max-w-4xl mx-auto p-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {company.logo_url ? (
            <img
              src={company.logo_url}
              alt={`${company.name} logo`}
              className="w-10 h-10 object-contain"
            />
          ) : (
            <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
              <Globe className="w-6 h-6 text-muted-foreground" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold">{company.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={planDetails.variant as any}>
                {planDetails.name}
              </Badge>
              {company.plan === 'free' && (
                <span className="text-sm text-muted-foreground">
                  {feedbackCount}/{feedbackLimit} feedback items
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="flex items-center gap-2"
            onClick={() => setPrioritizationOpen(true)}
          >
            <LineChart className="w-4 h-4" />
            Prioritize
          </Button>
          <Button
            variant="outline"
            className="flex items-center gap-2"
            onClick={() => navigate(`/${company.slug}`)}
          >
            <Globe className="w-4 h-4" />
            View Public Page
            <ArrowRight className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            className="flex items-center gap-2"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4" />
            Logout
          </Button>
        </div>

        <PrioritizationPopup
          open={prioritizationOpen}
          onOpenChange={setPrioritizationOpen}
          feedback={feedback}
          companyId={company.id}
          defaultScoreMethod={company.default_score_method}
          onCompanyUpdate={onCompanyUpdate}
        />
      </div>
    </div>
  );
};
